import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { Button } from "@/components/ui/button";
import { NovaExpression } from "@/components/nova/kit";

/**
 * NotFound — shared 404 surface.
 *
 * Mounted at the top-level `*` catchall AND rendered inline by
 * PublicIntakeForm when its `/:orgSlug` lookup returns nothing
 * (P1-WEB-1 — the bare-slug URL doubles as a public intake link, so
 * unresolved slugs should read as a warm 404, not a scary "Business
 * Not Found" error).
 *
 * Editorial voice: Fraunces italic headline, orbit-orange accents,
 * `.orbit-eyebrow` uppercase, warm outcome-led copy that hands the
 * visitor a next step instead of a dead end.
 */
const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    // Keep the console breadcrumb — useful when triaging "why did
    // /helpx 404" reports.
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-transparent text-foreground flex items-center justify-center px-4 py-16">
      <div className="mx-auto max-w-xl text-center">
        <div className="mb-8 flex justify-center">
          <NovaExpression id="curious" size={96} />
        </div>

        <p className="orbit-eyebrow">404</p>
        <h1 className="mt-3 font-editorial text-5xl leading-tight sm:text-6xl md:text-7xl">
          <em>Nothing lives here.</em>
        </h1>
        <p className="mt-6 text-base leading-relaxed text-white/80 md:text-lg">
          The address you tried isn&apos;t a page on Orbit &mdash; or it moved
          somewhere warmer. Head back to the front door and Nova can point
          you where you meant to go.
        </p>

        <div className="mt-10 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <Button asChild size="lg" className="bg-orbit-orange text-background hover:bg-orbit-orange/90">
            <Link to="/">
              <span>Back to Orbit</span>
              <OrbitIcon name="arrow.right" fallback={ArrowRight} className="ml-1 h-4 w-4" />
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline">
            <Link to="/orbit">Visit the site</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
